import { createContext, useContext, useEffect, useState } from "react";

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // Only reconstruct minimal user info from token if it exists
    const token = localStorage.getItem("authToken");
    if (!token) return null;
    
    try {
      // Decode JWT to get user info (don't store passwords!)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const isAdmin = localStorage.getItem("userIsAdmin") === "true";
      return { email: payload.email, id: payload.sub, is_admin: isAdmin };
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState(() => localStorage.getItem("authToken"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, []);

  // Store admin flag temporarily when setting token
  const [adminFlag, setAdminFlag] = useState(() => 
    localStorage.getItem("userIsAdmin") === "true"
  );

  useEffect(() => {
    if (token) {
      localStorage.setItem("authToken", token);
      if (adminFlag) {
        localStorage.setItem("userIsAdmin", "true");
      } else {
        localStorage.removeItem("userIsAdmin");
      }
      // Reconstruct user from token
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({ email: payload.email, id: payload.sub, is_admin: adminFlag });
      } catch {
        setUser(null);
      }
    } else {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userIsAdmin");
      setUser(null);
    }
  }, [token, adminFlag]);

  const login = async (username, password) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const loginEndpoint = apiUrl ? `${apiUrl}/api/auth/login` : '/.netlify/functions/login';

    const res = await fetch(loginEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    const responseText = await res.text();
    let data;

    try {
      data = responseText ? JSON.parse(responseText) : {};
    } catch (err) {
      throw new Error(
        `Login failed: invalid response from server (${res.status}). Response text: ${responseText}`
      );
    }

    if (!res.ok) throw new Error(data.error || "Login failed");

    // Store admin flag and token
    if (data.user?.is_admin) {
      setAdminFlag(true);
    } else {
      setAdminFlag(false);
    }
    setToken(data.token);
  };

  const logout = () => {
    setAdminFlag(false);
    setToken(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("userIsAdmin");
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);